/**
 * Types and functions for welding geometry to get rid of duplicate vertices
 * @module
 */
import RBush from "rbush";
import { RBush3D } from "rbush-3d";
import type { AABB2 } from "./aabb2Types";
import { aabb2New } from "./aabb2Types";
import type { AABB3 } from "./aabb3Types";
import { aabb3New } from "./aabb3Types";
import { DIST_EPSILON, DIST_EPSILON_32BIT } from "./core";
import type { Mesh3, QuadMesh3 } from "./mesh";
import { polyLine2IsClosed, polyLine2Vertex, polyLine2VertexCount } from "./polyLine2";
import type { PolyLine2 } from "./polyLine2Types";

interface PolyLine2IndexAABB2 extends AABB2 {
  index: number;
  vertexIndex: number;
}

function polyLine2IndexAABB2(
  minX: number,
  minY: number,
  maxX: number,
  maxY: number,
  index: number,
  vertexIndex: number,
): PolyLine2IndexAABB2 {
  return {
    minX,
    minY,
    maxX,
    maxY,
    index,
    vertexIndex,
  };
}

export function weldPolyLine2s(pls: PolyLine2[], tol: number = 100 * DIST_EPSILON): boolean {
  const rtree = new RBush<PolyLine2IndexAABB2>();
  let weldedVertices = false;

  // Insert vertices in tree
  const insertedVertices: PolyLine2IndexAABB2[] = [];
  for (let plIndex = 0; plIndex < pls.length; plIndex++) {
    const pl = pls[plIndex];

    let uniqueVertexCount = polyLine2VertexCount(pl);
    if (polyLine2IsClosed(pl)) {
      uniqueVertexCount -= 1;
    }

    for (let vertexIndex = 0; vertexIndex < uniqueVertexCount; vertexIndex++) {
      const [x, y] = polyLine2Vertex(pl, vertexIndex);
      const aabb = polyLine2IndexAABB2(
        x - DIST_EPSILON,
        y - DIST_EPSILON,
        x + DIST_EPSILON,
        y + DIST_EPSILON,
        plIndex,
        vertexIndex,
      );
      insertedVertices.push(aabb);
    }
  }
  rtree.load(insertedVertices);

  // Find and merge close vertices
  for (let i = 0; i < insertedVertices.length; i++) {
    const vertexAabb = insertedVertices[i];
    const currentPlIndex = vertexAabb.index;

    const searchAabb = aabb2New(
      vertexAabb.minX - tol,
      vertexAabb.minY - tol,
      vertexAabb.maxX + tol,
      vertexAabb.maxY + tol,
    );
    const results = rtree.search(searchAabb) as PolyLine2IndexAABB2[];

    if (results.length > 1) {
      weldedVertices = true;

      const minIndex = Math.min(...results.map((aabb) => aabb.index));
      // Only weld vertices once
      if (minIndex === currentPlIndex) {
        // Compute welded vertex as average of result vertices
        let centroidX = 0;
        let centroidY = 0;
        results.forEach((result) => {
          const [x, y] = polyLine2Vertex(pls[result.index], result.vertexIndex);
          centroidX += x;
          centroidY += y;
        });
        centroidX /= results.length;
        centroidY /= results.length;

        // Apply welded vertex to result vertices
        results.forEach((result) => {
          pls[result.index][2 * result.vertexIndex] = centroidX;
          pls[result.index][2 * result.vertexIndex + 1] = centroidY;
        });
      }
    }
  }

  return weldedVertices;
}

interface MeshIndexAABB3 extends AABB3 {
  meshIndex: number;
  vertexIndex: number;
}

class IndexDisjointSet {
  parent: number[];
  compactMapping?: number[];

  constructor(len: number) {
    this.parent = Array.from({ length: len }, (_, i) => i);
    this.compactMapping = undefined;
  }

  find(i: number): number {
    if (this.parent[i] === i) {
      return i;
    } else {
      const parent = this.find(this.parent[i]);
      this.parent[i] = parent; // cheap path compression
      return parent;
    }
  }

  union(i1: number, i2: number) {
    // Sorting by min is an invariant that allows easy compaction
    const parent = Math.min(this.find(i1), this.find(i2));
    this.parent[i1] = this.parent[i2] = parent;
  }

  compact() {
    if (this.compactMapping === undefined) {
      this.compactMapping = [];

      let nextIndex = 0;
      for (let i = 0; i < this.parent.length; i++) {
        console.assert(this.parent[i] <= i); // invariant
        if (this.parent[i] === i) {
          this.compactMapping.push(nextIndex++);
        } else {
          this.compactMapping.push(this.compactMapping[this.parent[i]]);
        }
      }
    }
  }

  eliminateDuplicates() {
    if (this.compactMapping === undefined) {
      this.compactMapping = [];

      for (let i = 0; i < this.parent.length; i++) {
        console.assert(this.parent[i] <= i); // invariant
        if (this.parent[i] === i) {
          this.compactMapping.push(i);
        } else {
          this.compactMapping.push(this.compactMapping[this.parent[i]]);
        }
      }
    }
  }
}

export enum WeldingDuplicateHandling {
  CHANGE_ONLY_POSITIONS,
  ALLOW_CHANGING_INDICES,
  REMOVE_DUPLICATE_VERTICES,
}

/**
 * Weld near-identical vertices in a mesh together.
 * @param tolerance Maximum Chebyshev distance for vertices to get welded.
 * @param duplicateHandling What will be done to duplicates?
 * @return Welded vertices
 */
export function weldVertices(
  verts: number[],
  tris: number[],
  duplicateHandling: WeldingDuplicateHandling,
  tolerance?: number,
): number[];
export function weldVertices(
  verts: Float32Array,
  tris: Uint32Array,
  duplicateHandling: WeldingDuplicateHandling,
  tolerance?: number,
): Float32Array;
export function weldVertices(
  verts: Float32Array | number[],
  tris: Uint32Array | number[],
  duplicateHandling: WeldingDuplicateHandling,
  tolerance?: number,
): Float32Array | number[] {
  const rtree = new RBush3D();

  const is32Bit = verts instanceof Float32Array;
  tolerance = tolerance ?? (is32Bit ? DIST_EPSILON_32BIT : DIST_EPSILON);

  const vertexCount = verts.length / 3;

  // Insert vertices in tree
  const insertedVertices: MeshIndexAABB3[] = [];
  for (let vertexIndex = 0; vertexIndex < vertexCount; vertexIndex++) {
    const x = verts[3 * vertexIndex];
    const y = verts[3 * vertexIndex + 1];
    const z = verts[3 * vertexIndex + 2];

    const aabb = aabb3New(
      x - DIST_EPSILON,
      y - DIST_EPSILON,
      z - DIST_EPSILON,
      x + DIST_EPSILON,
      y + DIST_EPSILON,
      z + DIST_EPSILON,
    ) as MeshIndexAABB3;
    aabb.vertexIndex = vertexIndex;

    insertedVertices.push(aabb);
  }
  rtree.load(insertedVertices);

  // Determine duplicates via disjoint set
  const disjointSet = new IndexDisjointSet(vertexCount);
  for (let vertexIndex = 0; vertexIndex < vertexCount; vertexIndex++) {
    const x = verts[3 * vertexIndex];
    const y = verts[3 * vertexIndex + 1];
    const z = verts[3 * vertexIndex + 2];

    const aabb = aabb3New(x - tolerance, y - tolerance, z - tolerance, x + tolerance, y + tolerance, z + tolerance);
    const result = rtree.search(aabb) as MeshIndexAABB3[];

    for (let i = 0; i < result.length - 1; i++) {
      disjointSet.union(result[i].vertexIndex, result[i + 1].vertexIndex);
    }
  }

  if (duplicateHandling === WeldingDuplicateHandling.REMOVE_DUPLICATE_VERTICES) {
    // Rewrite vertex indices
    disjointSet.compact();
    if (disjointSet.compactMapping) {
      for (let triIndex = 0; triIndex < tris.length; triIndex++) {
        const vertexIndex = tris[triIndex];
        tris[triIndex] = disjointSet.compactMapping[vertexIndex];
      }
    }
    // Remove duplicate vertices
    const deletedVertexIndices = new Set();
    for (let vertexIndex = 0; vertexIndex < vertexCount; vertexIndex++) {
      if (disjointSet.parent[vertexIndex] !== vertexIndex) {
        deletedVertexIndices.add(3 * vertexIndex);
        deletedVertexIndices.add(3 * vertexIndex + 1);
        deletedVertexIndices.add(3 * vertexIndex + 2);
      }
    }

    return verts.filter((_, i) => !deletedVertexIndices.has(i));
  } else if (duplicateHandling === WeldingDuplicateHandling.ALLOW_CHANGING_INDICES) {
    // Rewrite vertex indices
    disjointSet.eliminateDuplicates();
    if (disjointSet.compactMapping) {
      for (let triIndex = 0; triIndex < tris.length; triIndex++) {
        const vertexIndex = tris[triIndex];
        tris[triIndex] = disjointSet.compactMapping[vertexIndex];
      }
    }
    return verts;
  } else {
    for (let vertexIndex = 0; vertexIndex < vertexCount; vertexIndex++) {
      if (disjointSet.parent[vertexIndex] !== vertexIndex) {
        const weldedVertexIndex = disjointSet.parent[vertexIndex];
        for (let i = 0; i < 3; i++) {
          verts[3 * vertexIndex + i] = verts[3 * weldedVertexIndex + i];
        }
      }
    }
  }
  return verts;
}

/**
 * Weld near-identical vertices in a mesh together.
 * @param mesh
 * @param tolerance Maximum Chebyshev distance for vertices to get welded.
 * @param forceRemoveDuplicates If set to true, will remove duplicates even if they have mismatching normals or other data, as long as the position matches.
 */
export function weldMesh(mesh: Mesh3, tolerance: number, forceRemoveDuplicates: boolean = false) {
  const hasSupplementaryData =
    mesh.norms !== undefined || mesh.ids !== undefined || mesh.uvs1 !== undefined || mesh.uvs2 !== undefined;
  // We could support an additional mode where we average the normals at the welded point, but that is not currently needed.
  // We'll just overwrite the vertex position data, preserving normals, ids etc.
  const duplicateHandling =
    !hasSupplementaryData || forceRemoveDuplicates
      ? WeldingDuplicateHandling.REMOVE_DUPLICATE_VERTICES
      : WeldingDuplicateHandling.CHANGE_ONLY_POSITIONS;
  mesh.verts = weldVertices(mesh.verts, mesh.tris, duplicateHandling, tolerance);
}

/**
 * Weld near-identical vertices in a mesh together.
 * @param mesh
 * @param tolerance Maximum Chebyshev distance for vertices to get welded.
 * @param forceRemoveDuplicates If set to true, will remove duplicates even if they have mismatching normals or other data, as long as the position matches.
 */
export function weldQuadMesh(mesh: QuadMesh3, tolerance: number, forceRemoveDuplicates: boolean = false) {
  const hasSupplementaryData =
    mesh.norms !== undefined || mesh.ids !== undefined || mesh.uvs1 !== undefined || mesh.uvs2 !== undefined;
  // We could support an additional mode where we average the normals at the welded point, but that is not currently needed.
  // We'll just overwrite the vertex position data, preserving normals, ids etc.
  const duplicateHandling =
    !hasSupplementaryData || forceRemoveDuplicates
      ? WeldingDuplicateHandling.ALLOW_CHANGING_INDICES
      : WeldingDuplicateHandling.CHANGE_ONLY_POSITIONS;
  mesh.verts = weldVertices(mesh.verts, mesh.tris, duplicateHandling, tolerance);
  mesh.verts = weldVertices(mesh.verts, mesh.quads, duplicateHandling, tolerance);
}
